import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'
import styled from '@emotion/styled'
import { config } from '../../config.js'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import axios from '../../dataClient/dataClient'
import { getAdditionalDetailsInformation, getTierInformation } from '../../dataClient/apiCalls'
import { emptyPir, getPirStepperArray } from '../../util/pirConsts.js'
import { pirSchema } from '../../schemas/pirForm.js'
import pirTerminology from '../../util/pirTerminology'
import {
  FormSectionDiv,
  DarkBGPaddedFormDiv,
  FlexEndButtonErrorContainer,
  FlexColumn,
  Form,
  SpaceBetweenRowContainer,
  FlexEndContainer
} from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button from '../common/Button'
import BodyText from '../styles/Text'
import PropertyForm from '../pirForms/PropertyForm'
import OwnerForm from '../pirForms/OwnerForm'
import RequestorForm from '../pirForms/RequestorForm'
import RequestForm from '../pirForms/RequestForm'
import AutoStrata from '../pirForms/AutoStrata'
import ManualStrata from '../pirForms/ManualStrata'
import AdditionalRecipients from '../pirForms/AdditionalRecipients.js'
import SameEmailAlert from '../pir/SameEmailAlert.js'
import Carousel from '../common/Carousel.js'
import { getRuntimeEnv } from '../../runtimeEnv'

const BASE_URL = getRuntimeEnv('REACT_APP_API_URL')

const ReviewButtonContainer = styled(FlexEndContainer)`
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`

function PIRForm({
  pir = emptyPir,
  strata = 'info',
  onCompleteForm = () => {},
  saveFormattedAddress = () => {},
  addressVerification = true,
  strataVerification = true
}) {
  const [error, setError] = useState(strata === 'error' ? 'strataError' : undefined)
  const [visibleSameEmailAlert, setVisibleSameEmailAlert] = useState(false)
  const [dataForSubmission, setDataForSubmission] = useState()
  const [shouldVerify, setShouldVerify] = useState(pir === emptyPir)
  const [controller, setController] = useState()

  const [propertyChecked, setPropertyChecked] = useState(pir !== emptyPir)
  const [propertyLoading, setPropertyLoading] = useState()
  const [tierConfig, setTierConfig] = useState(null)
  const [additionalDetailsEnabled, setAdditionalDetailsEnabled] = useState(false)

  useEffect(() => {
    getTierInformation()
      .then(response => {
        setTierConfig(response.data.data || null)
      })
      .catch(error => {
        console.error('Failed to fetch tier information', error)
        setTierConfig(null)
      })

    getAdditionalDetailsInformation()
      .then(response => {
        setAdditionalDetailsEnabled(response.data.data?.enabled || false)
      })
      .catch(error => {
        console.error('Failed to fetch Additional Details information', error)
        setAdditionalDetailsEnabled(false)
      })
  }, [])

  const tierPricingEnabled = !!tierConfig?.enabled

  const methods = useForm({
    resolver: yupResolver(pirSchema),
    defaultValues: { ...pir, strataState: strata },
    context: { tierPricingEnabled }
  })
  const { handleSubmit, watch, setValue } = methods
  const address = watch('propertyInformation.address')
  const unit = watch('propertyInformation.unit')
  const postalCode = watch('propertyInformation.postalCode')
  const ownerEmail = watch('ownerInformation.email')
  const strataEmail = watch('strataInformation.email')
  const requestorEmail = watch('requestorInformation.email')
  const ownerSignatureSource = watch('ownerInformation.signatureSource')
  const strataState = watch('strataState')

  const onUpdateStrataState = newState => setValue('strataState', newState)

  const onError = (errors, e) => console.error('Error', errors, e)

  const handleCancelAlert = () => setVisibleSameEmailAlert(false)

  function cleanRequestInformation(data) {
    const cleanedData = { ...data }
    if (cleanedData.requestInformation) {
      const { tier, additionalDetails, tierOptions } = cleanedData.requestInformation
      const isEmpty = (!tier || tier === '') && (!additionalDetails || additionalDetails.trim() === '') && (!tierOptions || tierOptions.length === 0)
      if (isEmpty) {
        delete cleanedData.requestInformation
      }
    }
    return cleanedData
  }

  function verifyAndSubmit(data) {
    const cleanedData = cleanRequestInformation(data)
    if (!addressVerification || !error) {
      onCompleteForm({ pir: cleanedData })
    } else if (error === 'address') {
      console.error('The address does not match an address on record')
    } else if (error !== 'address') {
      setPropertyLoading(true)
      axios
        .post(`${BASE_URL}/property-data`, { address, unit })
        .then(({ data: returnedData }) => {
          saveFormattedAddress(returnedData.data.formattedAddress)
          setError()
          onCompleteForm({ pir: cleanedData })
        })
        .catch(e => {
          console.error('Failed verifying property address', e)
          if (e.status === 404) setError('address')
          else setError('error')
        })
        .finally(() => setPropertyLoading(false))
    }
  }

  function handleConfirmAlert() {
    setVisibleSameEmailAlert(false)
    verifyAndSubmit(dataForSubmission)
  }

  function onSubmit(data) {
    setDataForSubmission(data)
    if (
      (strataEmail === requestorEmail && ['manualStrata', 'autoStrata'].includes(strataState)) ||
      (ownerEmail === requestorEmail && ownerSignatureSource !== 'upload')
    )
      setVisibleSameEmailAlert(true)
    else verifyAndSubmit(data)
  }

  function verifyAddressOrStrata() {
    setPropertyLoading(true)

    if (controller) controller.abort()
    setError()
    const newController = new AbortController()
    setController(newController)

    axios
      .post(`${BASE_URL}/property-data`, { address, unit }, { signal: newController.signal })
      .then(({ data }) => {
        if (strataVerification) data.data.isStrata ? onUpdateStrataState('autoStrata') : onUpdateStrataState('autoNoStrata')
        saveFormattedAddress(data.data.formattedAddress)
        setPropertyLoading(false)
      })
      .catch(e => {
        if (e.name === 'CanceledError') {
          console.error('Cancelled request to retrieve property data', e)
        } else {
          if (e.status === 404) setError('address')
          else if (strataVerification) setError('strataError')
          else setError('verificationError')

          console.error('Failed retrieving property data', e)
          if (strataVerification) onUpdateStrataState('error')
          setPropertyLoading(false)
        }
      })
  }

  useEffect(() => {
    if (addressVerification) {
      const timeoutId = setTimeout(() => {
        if (postalCode !== '' && address !== '') {
          if (strataVerification && strataState === 'info') {
            onUpdateStrataState('processing')
            verifyAddressOrStrata()
          } else if (!strataVerification && !propertyChecked) {
            setPropertyChecked(true)
            verifyAddressOrStrata()
          }
        }
      }, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [postalCode])

  useEffect(() => {
    if (addressVerification) {
      if (strataVerification && strataState !== 'info' && shouldVerify) onUpdateStrataState('processing')

      const timeoutId = setTimeout(() => {
        if (shouldVerify) {
          const hasAddress = address !== ''
          const hasPostalCode = postalCode !== ''
          if (strataVerification) {
            if ((strataState !== 'info' && hasAddress) || (strataState === 'info' && hasAddress && hasPostalCode)) {
              onUpdateStrataState('processing')
              verifyAddressOrStrata()
            }
            if (strataState !== 'info' && !hasAddress) {
              if (controller) controller.abort()
              onUpdateStrataState('info')
              setPropertyLoading(false)
              setError()
            }
          } else {
            if ((propertyChecked && hasAddress) || (!propertyChecked && hasAddress && hasPostalCode)) {
              setPropertyChecked(true)
              verifyAddressOrStrata()
            }
            if (propertyChecked && !hasAddress) {
              if (controller) controller.abort()
              setPropertyChecked(false)
              setPropertyLoading(false)
              setError()
            }
          }
        } else {
          setShouldVerify(true)
        }
      }, 800)
      return () => clearTimeout(timeoutId)
    }
  }, [unit, address, 500])

  const disclaimer = config.pir.disclaimer.split('<br/>').map((line, index) => <BodyText key={index}>{line}</BodyText>)

  return (
    <FlexColumn>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit, onError)} gap="30px">
          <SpaceBetweenRowContainer gap="50px">
            <Carousel steps={getPirStepperArray()} activeStep={0} />
            <ReviewButtonContainer>
              <Button type="submit" padding="large" loading={propertyLoading} disabled={['info', 'processing', 'manualUnset'].includes(strataState)}>
                Review
                <ArrowForwardIosIcon />
              </Button>
            </ReviewButtonContainer>
          </SpaceBetweenRowContainer>
          <DarkBGPaddedFormDiv>
            {disclaimer}
            <FormSectionDiv error={error === 'address'}>
              <PropertyForm />
              {error === 'address' && <ErrorMessage>The entered address does not match our records. Please update your request.</ErrorMessage>}
            </FormSectionDiv>
            <FormSectionDiv>
              <OwnerForm />
            </FormSectionDiv>
            <FormSectionDiv>{addressVerification && strataVerification ? <AutoStrata /> : <ManualStrata />}</FormSectionDiv>
            <FormSectionDiv>
              <RequestorForm />
              <AdditionalRecipients />
            </FormSectionDiv>
            {(tierPricingEnabled || additionalDetailsEnabled) && (
              <FormSectionDiv>
                <RequestForm tierConfig={tierConfig} additionalDetailsEnabled={additionalDetailsEnabled} />
              </FormSectionDiv>
            )}
            <FlexEndButtonErrorContainer>
              <Button type="submit" padding="large" loading={propertyLoading} disabled={['info', 'processing', 'manualUnset'].includes(strataState)}>
                Review
                <ArrowForwardIosIcon />
              </Button>
              {visibleSameEmailAlert && (
                <SameEmailAlert
                  isVisible={visibleSameEmailAlert}
                  sameOwner={ownerEmail === requestorEmail && ownerSignatureSource !== 'upload'}
                  sameStrata={strataEmail === requestorEmail && ['autoStrata', 'manualStrata'].includes(strataState)}
                  onCancel={handleCancelAlert}
                  onConfirm={handleConfirmAlert}
                />
              )}
              {error && error !== 'strataError' && (
                <ErrorMessage>
                  {error === 'address'
                    ? 'The entered address does not match our records. Please update your request and try again.'
                    : error === 'verificationError'
                      ? 'An error occurred while verifying the address details. Please update your request and try again.'
                      : `An error occurred while submitting the ${pirTerminology.getRequestShortName()}. Please try again.`}
                </ErrorMessage>
              )}
            </FlexEndButtonErrorContainer>
          </DarkBGPaddedFormDiv>
        </Form>
      </FormProvider>
    </FlexColumn>
  )
}

PIRForm.propTypes = {
  pir: PropTypes.object,
  strata: PropTypes.oneOf(['info', 'processing', 'autoNoStrata', 'autoStrata', 'error', 'manualUnset', 'manualStrata', 'manualNoStrata']),
  onCompleteForm: PropTypes.func,
  saveFormattedAddress: PropTypes.func,
  addressVerification: PropTypes.bool
}

export default PIRForm
